import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import ScrollToTop from "@/components/feature/ScrollToTop";
import { employeeReviews } from "@/mocks/employeeReviews";
import { vacancies } from "@/mocks/vacancies";

const TELEGRAM_URL = "https://t.me/Vertttess";
const CONTRACT_URL = "/docs/dogovor-gph-hr.pdf";

const workConditions = [
  {
    icon: "ri-computer-line",
    title: "Формат работы",
    text: "Работа полностью удалённая. Полный рабочий день, есть ночные смены.",
  },
  {
    icon: "ri-calendar-schedule-line",
    title: "Гибкий график",
    text: "График 2/2, 3/3, 5/2. Количество часов сотрудник выбирает самостоятельно.",
  },
  {
    icon: "ri-dashboard-line",
    title: "Рабочее пространство",
    text: "Работа в CRM-системе Битрикс24. Часы работы и вся отчётность учитываются там.",
  },
  {
    icon: "ri-user-add-line",
    title: "Требования",
    text: "Работа с 18 лет. Также возможно оформление на 3-е лицо.",
  },
  {
    icon: "ri-wallet-3-line",
    title: "Оплата",
    text: "Почасовая ставка 350 ₽/час. Выплаты производятся еженедельно на счёт ИП.",
  },
  {
    icon: "ri-file-list-3-line",
    title: "Оформление",
    text: "Заключаем договор ГПХ (гражданско-правового характера) с каждым сотрудником.",
  },
];

function StarRating({ rating }: { rating: number }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <i
          key={star}
          className={`${
            star <= rating ? "ri-star-fill text-amber-400" : "ri-star-line text-foreground-300"
          } text-sm`}
        />
      ))}
    </div>
  );
}

function VacancyCard({ vacancy, index }: { vacancy: typeof vacancies[0]; index: number }) {
  const isOpen = vacancy.status === "open";
  return (
    <div
      className="bg-background-50 border border-background-200/70 rounded-lg p-5 md:p-6 flex flex-col md:flex-row md:items-start gap-5 transition-all hover:border-background-300/60"
      style={{ animationDelay: `${index * 0.1}s` }}
    >
      {/* Status indicator */}
      <div className="shrink-0 flex items-center gap-2 md:flex-col md:items-center md:min-w-[90px]">
        <div
          className={`w-3 h-3 rounded-full shrink-0 ${
            isOpen ? "bg-emerald-500" : "bg-foreground-300"
          }`}
        />
        <span
          className={`text-xs font-medium whitespace-nowrap ${
            isOpen ? "text-emerald-600" : "text-foreground-400"
          }`}
        >
          {isOpen ? "Открыта" : "Закрыта"}
        </span>
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <h3 className="font-heading text-lg md:text-xl text-foreground-900 mb-2">
          {vacancy.title}
        </h3>
        <p className="text-sm text-foreground-600 leading-relaxed mb-4">
          {vacancy.description}
        </p>
        <div className="flex flex-wrap gap-2">
          {vacancy.requirements.map((req, i) => (
            <span
              key={i}
              className="text-xs px-2.5 py-1 rounded-full bg-background-100 border border-background-200/70 text-foreground-600"
            >
              {req}
            </span>
          ))}
        </div>
      </div>

      {/* Action button */}
      {isOpen && (
        <div className="shrink-0 md:self-center">
          <a
            href={TELEGRAM_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium bg-primary-500 text-white rounded-md hover:bg-primary-600 transition-colors whitespace-nowrap"
          >
            <i className="ri-rocket-line" />
            Перейти к обучению
          </a>
        </div>
      )}
    </div>
  );
}

export default function VacanciesPage() {
  const [showPreloader, setShowPreloader] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => setShowPreloader(false), 800);
    return () => clearTimeout(timer);
  }, []);

  if (showPreloader) {
    return (
      <div className="fixed inset-0 z-[100] flex flex-col items-center justify-center bg-background-50">
        <img
          src="/readdy/6606b04b1f7f2f98f392e21c6ab06083.png"
          alt="Have Received"
          className="h-20 w-auto animate-pulse"
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <ScrollToTop />

      <main className="pt-24 pb-16">
        {/* Hero */}
        <section className="max-w-5xl mx-auto px-4 md:px-8 text-center mb-16 md:mb-24">
          <div className="mb-6">
            <img
              src="/readdy/6606b04b1f7f2f98f392e21c6ab06083.png"
              alt="Have Received"
              className="h-20 md:h-28 w-auto mx-auto"
            />
          </div>
        </section>

        {/* Open Vacancies */}
        <section className="max-w-4xl mx-auto px-4 md:px-8 mb-16 md:mb-24">
          <h2 className="font-heading text-2xl md:text-3xl text-foreground-900 text-center mb-3">
            Актуальные вакансии
          </h2>
          <p className="text-sm text-foreground-500 text-center mb-10">
            Выберите подходящую вакансию и начните обучение уже сегодня
          </p>
          <div className="flex flex-col gap-4">
            {vacancies.map((vacancy, idx) => (
              <VacancyCard key={vacancy.id} vacancy={vacancy} index={idx} />
            ))}
          </div>
        </section>

        {/* Work Conditions */}
        <section className="max-w-6xl mx-auto px-4 md:px-8 mb-16 md:mb-24">
          <h2 className="font-heading text-2xl md:text-3xl text-foreground-900 text-center mb-10">
            Условия работы
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {workConditions.map((item, idx) => (
              <div
                key={idx}
                className="bg-background-50 border border-background-200/70 rounded-lg p-5 hover:border-primary-300 transition-colors"
              >
                <div className="w-10 h-10 flex items-center justify-center bg-primary-100 rounded-md mb-4">
                  <i className={`${item.icon} text-lg text-primary-600`} />
                </div>
                <h3 className="font-medium text-foreground-900 mb-2">{item.title}</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">{item.text}</p>
              </div>
            ))}
          </div>

          {/* Contract download */}
          <div className="mt-10 text-center">
            <a
              href={CONTRACT_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-medium border border-primary-300 text-primary-700 rounded-md hover:bg-primary-50 transition-colors"
            >
              <i className="ri-file-download-line" />
              Скачать договор ГПХ
            </a>
          </div>
        </section>

        {/* Employee Reviews */}
        <section className="max-w-6xl mx-auto px-4 md:px-8 mb-16 md:mb-24">
          <h2 className="font-heading text-2xl md:text-3xl text-foreground-900 text-center mb-10">
            Отзывы сотрудников
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {employeeReviews.map((review) => (
              <div
                key={review.id}
                className="bg-background-50 border border-background-200/70 rounded-lg p-5 flex flex-col"
              >
                <div className="flex items-center gap-3 mb-4">
                  <img
                    src={review.avatar}
                    alt={review.name}
                    className="w-12 h-12 rounded-full object-cover shrink-0"
                  />
                  <div>
                    <p className="font-medium text-foreground-900 text-sm">{review.name}</p>
                    <p className="text-xs text-foreground-500">{review.experience}</p>
                  </div>
                </div>
                <StarRating rating={review.rating} />
                <p className="text-sm text-foreground-600 leading-relaxed mt-3 flex-1">
                  {review.text}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className="max-w-3xl mx-auto px-4 md:px-8 text-center">
          <div className="bg-primary-50 border border-primary-200/60 rounded-xl p-8 md:p-10">
            <h3 className="font-heading text-xl md:text-2xl text-foreground-900 mb-3">
              Хотите присоединиться к команде?
            </h3>
            <p className="text-sm md:text-base text-foreground-600 mb-6">
              Напишите нам в Telegram — расскажем подробности и поможем с оформлением.
            </p>
            <a
              href={TELEGRAM_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-6 py-3 text-sm font-medium bg-primary-500 text-white rounded-md hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              <i className="ri-telegram-line" />
              Написать в Telegram
            </a>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}